import React, {useEffect, useRef, useState} from 'react';
import styles from './ViewValidationResults.module.css';
import {
    CommandBarButton,
    DetailsList,
    IColumn, MessageBarType, SelectionMode,
    Spinner,
    Stack,
    StackItem, TooltipOverflowMode
} from "@fluentui/react";
import {IDropdownOption} from "@fluentui/react/lib/Dropdown";
import strings from '../../loc/strings';
import {NATURAL_PERSON_TYPE, SORT_ORDER_ASC, SORT_ORDER_DESC} from "../../constants/EiopaFnPConstants";
import EiopaFnPIcons from "../EiopaFnPIcons/EiopaFnPIcons";
import Pagination from "../Pagination/Pagination";
import {
    _errorColumnsNormal,
    _errorColumnsHistorical,
    _type,
    ErrorColumnFieldName,
    errorIdToColumnKeyMap
} from "./ViewValidationResultsUtils";
import {ErrorCsvRecordForm, ErrorsCsvRecord} from "../../model/ErrorsCsvRecord";
import {
    excelExportValidationResults,
    fetchValidationResult
} from '../../services/ViewValidationResultsServices/ViewValidationResultService';
import {useAuth} from "react-oidc-context";
import {useParams} from "react-router-dom";
import {displayMessageBar} from "../LayoutWithAuth/LayoutWithAuth";
import {IValidationResults, ValidationResults} from "../../model/ValidationResults";
import {TooltipHost} from "@fluentui/react/lib/Tooltip";

const errorMessageRowGap = {
    marginBottom: '5px'
}

const errorFieldColor: React.CSSProperties = {
    color: 'red',
};

function ViewValidationResults() {
    const auth = useAuth();
    const { encryptedId, fileName } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [filters, setFilters] = useState<IValidationResults>(new ValidationResults());
    const [columnsState, setColumnsState] = useState<IColumn[]>(_errorColumnsNormal);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [items, setItems] = useState<ErrorsCsvRecord[]>([]);
    const [form, setForm] = useState<ErrorCsvRecordForm>({
        itemsPerPage: itemsPerPage,
        page: currentPage,
        sortBy: columnsState[2].key as string,
        sortOrder: 'asc'
    });

    const customDecodeFileName = (encodedFileName: string) => {
        return encodedFileName.replaceAll(/%[0-9A-F]{2}/gi, match =>
            String.fromCodePoint(Number.parseInt(match.substring(1), 16))
        );
    };
    const isEncoded = /%[0-9A-F]{2}/gi.test(fileName ?? "");
    const decodedFileName = isEncoded ? customDecodeFileName(fileName ?? "") : fileName;


    useEffect(() => {
        const fetchValidationResults = async () => {
            try {
                const response = await fetchValidationResult(form, encryptedId ?? "", auth.user?.id_token);
                setIsLoading(false);
                setItems(response);
                setTotalItems(response[0].totalItems);
                setFilters(new ValidationResults());

                // Determine which columns to use based on the dataType from the response
                const dataType = response[0]?.dataType;
                if (dataType === 0) {
                    setColumnsState(_errorColumnsNormal);
                } else if (dataType === 1) {
                    setColumnsState(_errorColumnsHistorical);
                }
            } catch (error: any) {
                setIsLoading(false);
                setIsAuthorized(false);
                if (error.message === strings.messageBarUnauthorizedNCA) {
                    displayMessageBar(MessageBarType.error, error.message, false);
                }
                displayMessageBar(MessageBarType.error, error.message, false);
            }
        };

        fetchValidationResults();
    }, [form, encryptedId, auth.user?.id_token]);

    const _onColumnHeaderClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
        if(column === undefined ||  column.fieldName === ErrorColumnFieldName.FailedRules || column.fieldName === ErrorColumnFieldName.SubmissionDate || column.fieldName === ErrorColumnFieldName.DateOfBirth || column.fieldName === ErrorColumnFieldName.DateOfAssessment) return;

        setIsLoading(true);

        let isSortedDescending = column.isSortedDescending;

        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }

        let columns = columnsState.map(col => {
            col.isSorted = col.key === column.key;
            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        });

        let sortBy = columnsState.filter(c => c.isSorted)[0].key as string;
        let sortOrder = columnsState.filter(c => c.isSorted)[0].isSortedDescending ? SORT_ORDER_DESC : SORT_ORDER_ASC;

        setCurrentPage(1);

        setForm({
            ...form,
            page: 1,
            sortBy: sortBy,
            sortOrder: sortOrder
        });
    };

    function mapValidation(statusCode: number) {
        switch (statusCode) {
            case 0:
                return <EiopaFnPIcons iconName={"success"} iconColor={"#107C10"}/>;
            case 1:
                return <EiopaFnPIcons iconName={"failed"} iconColor={"#D83B01"}/>;
            default:
                return "";
        }
    }

    function _renderItemColumn(item?: ErrorsCsvRecord, index?: number, column?: IColumn) {
        const getTooltipContent = (content: string | JSX.Element) => (
            <TooltipHost content={content} overflowMode={TooltipOverflowMode.Parent}>
                <span style={overflowStyle}>{content}</span>
            </TooltipHost>
        );

        const fieldValue = item ? item[column?.fieldName as keyof typeof item] : undefined;

        if (!item || !column || column.fieldName === undefined) {
            return getTooltipContent(fieldValue ?? "");
        }

        let displayValue = fieldValue;

        // Convert fieldValue to string if it's a number
        if (typeof fieldValue === 'number') {
            displayValue = fieldValue.toString();
        }

        // Define overflow styles
        const overflowStyle: React.CSSProperties = {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        };

        // Render icon in the Validation column
        if (column.fieldName === ErrorColumnFieldName.Validation) {
            return getTooltipContent(mapValidation(item.validationResults.length > 0 ? 1 : 0));
        }

        // Handle rendering for the Failed Rules column if validation results exist
        if (column.fieldName === ErrorColumnFieldName.FailedRules && item.validationResults.length > 0) {
            // Get unique error messages
            const uniqueErrorMessages = Array.from(new Set(item.validationResults.map(result => result.errorMessage)));

            // Concatenate all error messages into one string if there are multiple different errors
            const allErrorMessages = uniqueErrorMessages.map((message, index) => {
                return uniqueErrorMessages.length > 1 ? `${index + 1}. ${message}` : message;
            }).join('<br>');

            // Render error messages with tooltip
            return (
                <span>
                    <div style={errorMessageRowGap}>
                        <div style={overflowStyle}>
                            {getTooltipContent(<span dangerouslySetInnerHTML={{ __html: allErrorMessages }} />)}
                        </div>
                    </div>
                </span>
            );
        }

        // Iterate through validation results to handle error coloring
        if (item.validationResults.length > 0) {
            for (const validationResult of item.validationResults) {
                const affectedColumn = validationResult.columnsAffected;
                const columnKeyAffectedByError = errorIdToColumnKeyMap[affectedColumn];

                // Apply error color if the column is affected
                if (column.key === columnKeyAffectedByError) {
                    return getTooltipContent(<span style={{ ...errorFieldColor }}>{item[column.fieldName]}</span>);
                }
            }
        }

        return getTooltipContent(displayValue);
    }


    function _onExportListClick() {
        try {
            if (!fileName) {
                throw new Error("fileName is undefined");
            }

            displayMessageBar(MessageBarType.info, strings.messageBarExportListSpinner, true);

            excelExportValidationResults(form, encryptedId, auth.user?.id_token).then((success) => {
                const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                const message: string = success ? strings.messageBarExportListSuccess : strings.messageBarExportListError;
                displayMessageBar(messageBarType, message, false);
            });
        } catch (error) {
        }
    }


    function _onPageChange(selectedItem: number) {
        setIsLoading(true);

        setCurrentPage(selectedItem);

        setForm(prevState => ({
            ...prevState,
            page: selectedItem
        }));
    }

    function _onChangeItemsPerPage(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
        setIsLoading(true);

        const itemsPerPage = item.key as number;

        setItemsPerPage(item.key as number);
        setCurrentPage(1);

        setForm(prevState => ({
            ...prevState,
            itemsPerPage: itemsPerPage,
            page: 1
        }));
    }

    function _onRenderCSVIcon() {
        return <EiopaFnPIcons iconName={"CSV"} />;
    }

    return (
        <Stack>
            <StackItem>
                <Stack horizontal style={{justifyContent: "space-between", alignItems: "center"}}>
                    <StackItem className={styles.ViewValidationResultsHeader}>
                        {`${strings.fileDetails} (${decodedFileName})`}
                    </StackItem>
                    <StackItem>
                        <CommandBarButton
                            text={strings.excelExportButtonText}
                            className={styles.CmdBarButton}
                            onRenderIcon={() => _onRenderCSVIcon()}
                            onClick={() => _onExportListClick()}
                            disabled={!isAuthorized}
                            id="export-list-validation"
                        />
                    </StackItem>
                </Stack>
            </StackItem>
            <StackItem>
                {
                    isLoading ?
                        <Spinner label={strings.requestResultLoading} size={2} styles={{root: {padding: 20}}}></Spinner>
                        :
                        <Stack>
                            <StackItem>
                                <DetailsList
                                    layoutMode={1}
                                    // show overflowing header
                                    columns={columnsState.map((column) => ({
                                        ...column,
                                        onRenderHeader: () => (
                                            <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={column.name}>{column.name}</TooltipHost>
                                        ),
                                    }))}
                                    items={items}
                                    selectionMode={SelectionMode.none}
                                    selectionPreservedOnEmptyClick={true}
                                    onRenderItemColumn={_renderItemColumn}
                                    onColumnHeaderClick={(event, column) => _onColumnHeaderClick(event, column)}
                                />
                            </StackItem>
                            <StackItem>
                                <Pagination
                                    currentPage={currentPage}
                                    onPageChange={_onPageChange}
                                    itemsPerPage={itemsPerPage}
                                    onChangeItemsPerPage={_onChangeItemsPerPage}
                                    totalItems={totalItems}
                                />
                            </StackItem>
                        </Stack>
                }
            </StackItem>
        </Stack>
    );
}

export default ViewValidationResults;